import Button from '@ingka/button';
import Loading, { LoadingBall } from '@ingka/loading';
import information from '@ingka/ssr-icon/paths/information';
import Tooltip from '@ingka/tooltip';
import type { LotteryEditPhase } from '@lotta/feature-lottery-rules';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import { DateTime } from 'luxon';
import { memo, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../LotteryForm.module.scss';
import { ControlledDrawTimeDropdown } from '../../components/controlled-draw-time-dropdown/ControlledDrawTimeDropdown';
import { ControlledQuantityStepper } from '../../components/controlled-quantity-stepper/ControlledQuantityStepper';
import { ControlledYearMonthDropdown } from '../../components/controlled-year-month-dropdown/ControlledYearMonthDropdown';
import { LotteryFormContext, allowFieldInput } from '../../lottery-form-context';
import { lastDayOfMonth } from '../../utils';
import { useFormModule } from '../../utils/use-form-module';
import type { FormModuleProps, ModuleTransformer } from '../types';
import { MonthlyDrawDaysPreview } from './draw-days-preview/MonthlyDrawDaysPreview';
import { fromPayload } from './from-payload';
import { toPayload } from './to-payload';

export const MODULE_NAME = 'monthly-occurrence';

export type MonthlyOccurrenceFormType = Static<typeof MonthlyOccurrenceFormSchema>;
export const MonthlyOccurrenceFormSchema = Type.Object(
  {
    // occurrenceType: Type.Literal('MONTHLY'),
    startDate: Type.String({
      format: 'date',
      formatMinimum: DateTime.now().toISODate(),
      formatMaximum: { $data: '1/endDate' },
      errorMessage: {
        format: 'form-error-message-date-format',
        formatMinimum: 'form-start-month-error-message-minimum',
        formatMaximum: 'form-start-month-error-message-maximum',
      },
    }),
    endDate: Type.String({
      format: 'date',
      formatMinimum: { $data: '1/startDate' },
      errorMessage: {
        format: 'form-error-message-date-format',
        formatMinimum: 'form-end-month-error-message-minimum',
      },
    }),
    drawDay: Type.Integer({ minimum: 1, maximum: 14 }),
    drawTime: Type.RegEx(/^(0[0-9]|1[0-9]|2[0-3]):00$/, {
      errorMessage: {
        pattern: 'form-error-message-required',
      },
    }),
  },
  { additionalProperties: false, $id: 'MonthlyOccurrenceFormSchema' }
);

const editEnabledConfig: Record<keyof MonthlyOccurrenceFormType, LotteryEditPhase[]> = {
  startDate: ['OPEN', 'COPY'],
  endDate: ['OPEN', 'LIMITED', 'COPY'],
  drawDay: ['OPEN', 'LIMITED', 'COPY'],
  drawTime: ['OPEN', 'LIMITED', 'COPY'],
};

const getSchema = (initialValues: MonthlyOccurrenceFormType | undefined) => {
  if (!initialValues) {
    return MonthlyOccurrenceFormSchema;
  }
  // For edit lottery, modify validation for startDate to allow leaving it unchanged
  return Type.Object(
    {
      ...MonthlyOccurrenceFormSchema.properties,
      startDate: Type.String({
        ...MonthlyOccurrenceFormSchema.properties.startDate,
        formatMinimum: DateTime.min(
          DateTime.fromISO(initialValues.startDate),
          DateTime.now()
        ).toISODate(),
      }),
    },
    { additionalProperties: false, $id: 'UpdateWeeklyOccurrenceFormSchema' }
  );
};

export const MonthlyOccurrenceFormTransformer: ModuleTransformer<MonthlyOccurrenceFormType> = {
  fromPayload,
  toPayload,
};

const defaultValues: MonthlyOccurrenceFormType = {
  startDate: '',
  endDate: '',
  drawDay: 1,
  // eslint-disable-next-line i18next/no-literal-string
  drawTime: '00:00',
};

export type MonthlyOccurrenceFormProps = FormModuleProps<MonthlyOccurrenceFormType>;

export const MonthlyOccurrenceForm: React.FC<MonthlyOccurrenceFormProps> = memo(
  ({ initialValues, retailUnit, values, onChange, onValidation }) => {
    const lotteryFormContext = useContext(LotteryFormContext);
    const { data: retailUnitData, isLoading: retailUnitIsLoading } = useGetRetailUnitQuery({
      retailUnit,
    });
    const { t } = useTranslation();

    const schema = useMemo(() => getSchema(initialValues), [initialValues]);

    const {
      control,
      formState: { errors, touchedFields },
      trigger,
    } = useFormModule({
      moduleName: MODULE_NAME,
      defaultValues: values || defaultValues,
      schema,
      onChange,
      onValidation,
    });

    const triggerValidation = (fieldName: keyof typeof touchedFields) => {
      touchedFields[fieldName] && trigger(fieldName);
    };

    if (retailUnitIsLoading) {
      return (
        <Loading text={`${t('general-fetching-template')}`}>
          <LoadingBall />
        </Loading>
      );
    }

    const timezone = retailUnitData?.retailUnit.timeZone ?? '';

    return (
      <form>
        <div className={styles['tool-tip-container']}>
          <div className={styles['form-elements']}>
            <ControlledYearMonthDropdown
              name="startDate"
              control={control}
              disabled={!allowFieldInput(editEnabledConfig.startDate, lotteryFormContext)}
              labelKeys={{
                month: 'general-start-month',
                year: 'general-start-year',
              }}
              placeholderKeys={{
                month: 'form-month-placeholder',
                year: 'form-year-placeholder',
              }}
              day={1}
              onChange={() => {
                triggerValidation('endDate');
              }}
            />
          </div>
          <div className={styles['tool-tip']}>
            <Tooltip tooltipText={t('lottery-form-start-month-tool-tip')}>
              <Button iconOnly ssrIcon={information} type="tertiary" small />
            </Tooltip>
          </div>
        </div>
        <div className={styles['tool-tip-container']}>
          <div className={styles['form-elements']}>
            <ControlledYearMonthDropdown
              name="endDate"
              control={control}
              disabled={!allowFieldInput(editEnabledConfig.endDate, lotteryFormContext)}
              labelKeys={{
                month: 'general-end-month',
                year: 'general-end-year',
              }}
              placeholderKeys={{
                month: 'form-month-placeholder',
                year: 'form-year-placeholder',
              }}
              day={lastDayOfMonth}
              onChange={() => triggerValidation('startDate')}
            />
          </div>
          <div className={styles['tool-tip']}>
            <Tooltip
              tooltipText={t('lottery-form-end-month-tool-tip')}
              ssrIcon={information}
              position="trailing"
            >
              <Button iconOnly ssrIcon={information} type="tertiary" small />
            </Tooltip>
          </div>
        </div>
        <div className={styles['form-elements']}>
          <ControlledQuantityStepper
            name="drawDay"
            control={control}
            disabled={!allowFieldInput(editEnabledConfig.drawDay, lotteryFormContext)}
            label={t('lottery-form-draw-day-stepper-label')}
            minVal={MonthlyOccurrenceFormSchema.properties.drawDay.minimum ?? 1}
            maxVal={MonthlyOccurrenceFormSchema.properties.drawDay.maximum ?? 31}
          />
        </div>
        <div className={styles['tool-tip-container']}>
          <div className={styles['form-elements']}>
            <ControlledDrawTimeDropdown
              name="drawTime"
              control={control}
              disabled={!allowFieldInput(editEnabledConfig.drawTime, lotteryFormContext)}
              errorMessage={errors.drawTime?.message as string}
              timeZone={timezone}
            />
          </div>
          <div className={styles['tool-tip']}>
            <Tooltip
              tooltipText={t('lottery-form-draw-time-dropdown-tooltip')}
              ssrIcon={information}
              position="trailing"
            >
              <Button iconOnly ssrIcon={information} type="tertiary" small />
            </Tooltip>
          </div>
        </div>
        <MonthlyDrawDaysPreview control={control} timezone={timezone} />
      </form>
    );
  }
);

export const module = {
  name: MODULE_NAME,
  transformer: MonthlyOccurrenceFormTransformer,
  form: MonthlyOccurrenceForm,
} as const;
